// Resource / job status notices — distinct from conversational bot bubbles and step fiches.
//
// Card structure:
//   top-left label  : resource + operation  (CHECK RECEPTOR, RECEPTOR PREPARATION, DOCKING…)
//   top-right pill  : status value (Ready / processing / pending / Error…)
//   headline        : short sentence (Récepteur déjà en session, Préparation du récepteur lancée…)
//   rows            : PDB, Statut, Action, Sequence… — never IDs
//
// Color driven by status, not operation kind:
//   success  → green   (Ready / reuse / delete ok)
//   warning  → orange  (processing / pending)
//   danger   → red     (Error / Cancelled)
const STEP_BLOCK_RE = /^Step\s+\d+\s+—\s+([^\n]+)\n+([\s\S]+)$/i;
// IDs the user never sees in the MexaScreen panel — strip them from cards.
const ID_LABELS = new Set([
    'receptor id', 'peptide id', 'docking id', 'screening id',
    'binding site id', 'resource', 'ressource', 'receptor', 'job id',
]);
function escapeHtml(text) {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}
/** Escape then render **bold**, *italic*, `code` in notice headlines / row values. */
function formatInlineMarkdown(text) {
    return escapeHtml(text)
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        .replace(/`(.+?)`/g, '<code>$1</code>');
}
function normalizeStatus(raw) {
    const s = raw === null || raw === void 0 ? void 0 : raw.trim();
    if (!s)
        return undefined;
    const lower = s.toLowerCase();
    // Same vocabulary as the MexaScreen left panel (Ready / processing / …).
    if (['in_progress', 'running', 'processing'].includes(lower))
        return 'processing';
    if (lower === 'pending')
        return 'pending';
    if (['completed', 'complete', 'done', 'ok', 'ready'].includes(lower))
        return 'Ready';
    if (['failed', 'error', 'job_submission_failed'].includes(lower))
        return 'Error';
    if (['cancelled', 'canceled'].includes(lower))
        return 'Cancelled';
    return s;
}
const STATUS_ROW_LABELS = new Set([
    'statut',
    'status',
    'job status',
    'statut job',
    'statut actuel',
]);
function normalizeRows(rows) {
    return rows.map((r) => {
        var _a;
        if (!STATUS_ROW_LABELS.has(r.label.toLowerCase()))
            return r;
        return Object.assign(Object.assign({}, r), { value: (_a = normalizeStatus(r.value)) !== null && _a !== void 0 ? _a : r.value });
    });
}
function filterRows(rows) {
    return normalizeRows(rows.filter(r => !ID_LABELS.has(r.label.toLowerCase())));
}
// ── Color class — driven by status / kind ────────────────────────────────────
function noticeColorClass(notice) {
    var _a;
    const s = ((_a = notice.status) !== null && _a !== void 0 ? _a : '').toLowerCase();
    const k = notice.kind;
    if (k === 'error' || ['failed', 'error', 'job_submission_failed', 'cancelled', 'canceled'].includes(s)) {
        return 'chat-resource-notice-danger';
    }
    if (k === 'pending' || ['in_progress', 'processing', 'pending', 'running'].includes(s)) {
        return 'chat-resource-notice-warning';
    }
    return 'chat-resource-notice-success';
}
// ── Resource + action label (top-left of each card) ──────────────────────────
export function resourceActionLabel(notice, lang = 'en') {
    var _a;
    const { resource: r, kind: k } = notice;
    const fr = lang === 'fr';
    // Details cards already identify the item in `title` (Peptide #3 · SEQ).
    if (k === 'info' && notice.title && /^(peptide|docking)\s*#/i.test(notice.title)) {
        return notice.title;
    }
    if (r === 'receptor') {
        if (k === 'check')
            return fr ? 'STATUT RÉCEPTEUR' : 'RECEPTOR STATUS';
        if (k === 'delete') {
            if (notice.title && /deleted|supprim/i.test(notice.title)) {
                return notice.title.toUpperCase();
            }
            return fr ? 'SUPPRIMER RÉCEPTEUR' : 'DELETE RECEPTOR';
        }
        return fr ? 'PRÉPARATION RÉCEPTEUR' : 'RECEPTOR PREPARATION';
    }
    if (r === 'peptide') {
        if (k === 'check')
            return fr ? 'STATUT DESIGN' : 'DESIGN STATUS';
        if (k === 'delete') {
            if (notice.title && /deleted|supprim/i.test(notice.title)) {
                return notice.title.toUpperCase();
            }
            return fr ? 'SUPPRIMER PEPTIDE' : 'DELETE PEPTIDE';
        }
        return fr ? 'DESIGN PEPTIDE' : 'PEPTIDE DESIGN';
    }
    if (r === 'docking') {
        if (k === 'check')
            return fr ? 'STATUT DOCKING' : 'DOCKING STATUS';
        if (k === 'delete') {
            if (notice.title && /deleted|supprim/i.test(notice.title)) {
                return notice.title.toUpperCase();
            }
            return fr ? 'SUPPRIMER DOCKING' : 'DELETE DOCKING';
        }
        return 'DOCKING';
    }
    if (r === 'binding_site') {
        if (k === 'check')
            return fr ? 'STATUT BINDING SITE' : 'BINDING SITE STATUS';
        return 'BINDING SITE';
    }
    if (r === 'screening') {
        if (k === 'check')
            return fr ? 'STATUT SCREENING' : 'SCREENING STATUS';
        if (k === 'delete' && notice.title && /deleted|supprim/i.test(notice.title)) {
            return notice.title.toUpperCase();
        }
        return 'SCREENING';
    }
    if (r === 'generic' && k === 'info' && ((_a = notice.title) === null || _a === void 0 ? void 0 : _a.trim())) {
        return notice.title.trim().toUpperCase();
    }
    if (/screening/i.test(notice.title || ''))
        return 'SCREENING';
    if (/job status|statuts?\s+job/i.test(notice.title || '')) {
        return fr ? 'STATUTS JOBS' : 'JOB STATUSES';
    }
    return 'INFO';
}
/** Strip legacy "contrôle" headlines from LangGraph; keep progress headlines on check cards. */
function sanitizeHeadline(notice) {
    const h = notice.headline.trim();
    if (!h)
        return '';
    if (/contrôle|résultat\s+du\s+check|ci-dessus|^statut\s*:/i.test(h))
        return '';
    return h;
}
/** Always show operational cards (launch / reuse / error) — never hide them for banners. */
export function shouldHideLaunchNoticeForActionBanner(_notices, _currentStep) {
    return false;
}
/** Short intro bubble shown before notice cards (replaces redundant ai_response).
 *
 * Operational turns (launch / reuse / error / check / delete) → card only, no bubble.
 */
export function noticeIntroMessage(notices, opts) {
    if (!notices.length)
        return null;
    const n = notices[0];
    void opts;
    // Uniform: status / launch / reuse / error / delete → card only.
    if (n.kind === 'check' ||
        n.kind === 'launch' ||
        n.kind === 'reuse' ||
        n.kind === 'error' ||
        n.kind === 'delete' ||
        n.kind === 'pending') {
        return null;
    }
    // « check all » / bulk job statuses — card only.
    if (n.kind === 'info' && /job status/i.test(n.title || '')) {
        return null;
    }
    if (n.kind === 'info') {
        // Details cards (Peptide #N · SEQ) — conversational bubble comes from ai_response.
        if (/^(peptide|docking)\s*#/i.test(n.title || '')) {
            return null;
        }
        // Screening ready / similar — card + form banner carry the UX.
        if (/screening/i.test(n.title || '') || /prêt à lancer|ready to launch/i.test(n.headline || '')) {
            return null;
        }
        return null;
    }
    return null;
}
// ── Status pill class ─────────────────────────────────────────────────────────
function statusPillClass(status) {
    const s = (status !== null && status !== void 0 ? status : '').toLowerCase();
    if (['completed', 'complete', 'done', 'ok', 'ready'].includes(s))
        return 'chat-notice-status-done';
    if (['failed', 'error', 'job_submission_failed', 'cancelled', 'canceled'].includes(s))
        return 'chat-notice-status-failed';
    if (['in_progress', 'processing', 'pending', 'running'].includes(s))
        return 'chat-notice-status-progress';
    return 'chat-notice-status-neutral';
}
// ── Fallback parsers (heuristic on ai_response until agent sends chat_notices) ──
function parseReceptorReuse(_stepTitle, body) {
    const m = body.match(/Récepteur\s+(?:#\d+\s+)?\(([^)]+)\)\s+déjà en session\s+\(statut\s*:\s*([^)]+)\)\s*[—–-]\s*aucune requête envoyée/i);
    if (!m)
        return null;
    return {
        kind: 'reuse',
        resource: 'receptor',
        title: 'RECEPTOR PREPARATION',
        headline: 'Receptor already in session',
        status: normalizeStatus(m[2]),
        rows: filterRows([
            { label: 'PDB', value: m[1].trim() },
            { label: 'Status', value: m[2].trim() },
            { label: 'Action', value: 'No request sent' },
        ]),
    };
}
function parseReceptorLaunch(_stepTitle, body) {
    if (!/POST\s+.+\/prepare-receptor|Job lancé|Récepteur\s+#?\d*\s*prêt/i.test(body))
        return null;
    const pdbMatch = body.match(/PDB\s*:\s*(\S+)/i);
    const statusMatch = body.match(/statut\s*:\s*([^\n.]+)/i);
    const sitesMatch = body.match(/(\d+)\s+binding site/i);
    const rows = [];
    if (pdbMatch)
        rows.push({ label: 'PDB', value: pdbMatch[1] });
    if (statusMatch)
        rows.push({ label: 'Status', value: statusMatch[1].trim() });
    if (sitesMatch)
        rows.push({ label: 'Binding sites', value: sitesMatch[1] });
    const failed = /échoué|failed|error/i.test(body);
    const status = normalizeStatus(statusMatch === null || statusMatch === void 0 ? void 0 : statusMatch[1]);
    return {
        kind: failed ? 'error' : 'launch',
        resource: 'receptor',
        title: 'RECEPTOR PREPARATION',
        headline: failed ? 'Preparation failed' : 'Receptor preparation launched',
        status,
        rows: rows.length ? rows : [{ label: 'Detail', value: body.split('\n').slice(-2).join(' ').trim() }],
    };
}
function parseResourceCheck(body) {
    var _a, _b, _c;
    // Receptor — bold/verbose format from resource_ops
    const receptorBold = body.match(/Receptor\s+(?:#\d+\s+)?[—–-]\s+statut\s*:\s*\*{0,2}([^*(\n]+)\*{0,2}\s*\(PDB\s+([^,)]+)(?:,\s*([^)]+))?\)/i);
    if (receptorBold) {
        const rows = [
            { label: 'PDB', value: receptorBold[2].trim() },
            { label: 'Status', value: receptorBold[1].trim() },
        ];
        if (receptorBold[3])
            rows.push({ label: 'Binding sites', value: receptorBold[3].trim() });
        return {
            kind: 'check', resource: 'receptor',
            title: 'CHECK RECEPTOR', headline: '',
            status: normalizeStatus(receptorBold[1]),
            rows,
        };
    }
    // Receptor — simple format
    const receptorSimple = body.match(/(?:Check|check)\s+receptor\s+(?:#\d+\s+)?[—–-]\s*statut\s*:\s*([^\n.]+)(?:\.\s*(.+))?/i);
    if (receptorSimple) {
        const extra = (_a = receptorSimple[2]) === null || _a === void 0 ? void 0 : _a.trim();
        const rows = [];
        const pdb = extra === null || extra === void 0 ? void 0 : extra.match(/PDB\s*:\s*(\S+)/i);
        if (pdb)
            rows.push({ label: 'PDB', value: pdb[1] });
        rows.push({ label: 'Status', value: receptorSimple[1].trim() });
        const sites = extra === null || extra === void 0 ? void 0 : extra.match(/(\d+)\s+binding site/i);
        if (sites)
            rows.push({ label: 'Binding sites', value: sites[1] });
        return {
            kind: 'check', resource: 'receptor',
            title: 'CHECK RECEPTOR', headline: '',
            status: normalizeStatus(receptorSimple[1]),
            rows,
        };
    }
    // Peptide check
    const peptideCheck = body.match(/(?:Check|check)\s+peptide\s+(?:#\d+\s+)?[—–-]\s*statut\s*:\s*([^\n.]+)/i);
    if (peptideCheck) {
        const rows = [];
        const seqMatch = body.match(/s[eé]quence\s*:\s*(\S+)/i);
        if (seqMatch)
            rows.push({ label: 'Sequence', value: seqMatch[1] });
        rows.push({ label: 'Status', value: peptideCheck[1].trim() });
        return {
            kind: 'check', resource: 'peptide',
            title: 'CHECK PEPTIDE', headline: '',
            status: normalizeStatus(peptideCheck[1]),
            rows,
        };
    }
    // Delete receptor
    const deleteReceptor = body.match(/Récepteur\s+(?:#\d+\s+)?\(([^)]+)\)\s+supprimé|Suppression\s+du\s+récepteur\s+(?:PDB\s+)?([A-Z0-9]+)/i);
    if (deleteReceptor) {
        const pdb = (_c = ((_b = deleteReceptor[1]) !== null && _b !== void 0 ? _b : deleteReceptor[2])) === null || _c === void 0 ? void 0 : _c.trim();
        return {
            kind: 'delete', resource: 'receptor',
            title: 'DELETE RECEPTOR',
            headline: pdb ? `Receptor deleted (PDB: ${pdb})` : 'Receptor deleted',
            status: 'completed',
            rows: pdb ? [{ label: 'PDB', value: pdb }] : [],
        };
    }
    // Error line
    const errLine = body.match(/^Erreur\s+(check|stop|delete|relaunch)\s+(\w+)\s+#?(\S+)\s*:\s*(.+)$/i);
    if (errLine) {
        const res = errLine[2].toLowerCase();
        const resource = res.includes('peptide') ? 'peptide'
            : res.includes('docking') ? 'docking'
                : res.includes('receptor') ? 'receptor'
                    : 'generic';
        const op = errLine[1].toLowerCase();
        const notice = {
            kind: 'error', resource,
            title: 'INFO', headline: 'The request did not succeed',
            status: 'failed',
            rows: [{ label: 'Detail', value: errLine[4].trim() }],
        };
        notice.title = resourceActionLabel(Object.assign(Object.assign({}, notice), { kind: op === 'delete' ? 'delete' : 'check' }));
        return notice;
    }
    return null;
}
function parseJobPending(body) {
    var _a, _b;
    if (!/n'est pas encore prêt|En attente d'une action MexaScreen/i.test(body))
        return null;
    const statusMatch = (_a = body.match(/statut\s*:\s*([^)]+)\)/i)) !== null && _a !== void 0 ? _a : body.match(/statut\s*:\s*([^\n.]+)/i);
    const isPeptide = /peptide/i.test(body);
    const isError = /échoué|failed/i.test(body);
    const resource = isPeptide ? 'peptide' : 'receptor';
    return {
        kind: isError ? 'error' : 'pending',
        resource,
        title: resource === 'peptide' ? 'PEPTIDE DESIGN' : 'RECEPTOR PREPARATION',
        headline: isError ? 'Failed — action required' : 'Job in progress…',
        status: (_b = normalizeStatus(statusMatch === null || statusMatch === void 0 ? void 0 : statusMatch[1])) !== null && _b !== void 0 ? _b : (isError ? 'failed' : 'in_progress'),
        rows: [],
    };
}
function parseStepBlock(text) {
    var _a, _b, _c;
    const m = text.trim().match(STEP_BLOCK_RE);
    if (!m)
        return null;
    const stepTitle = m[1].trim();
    const body = m[2].trim();
    return ((_c = (_b = (_a = parseReceptorReuse(stepTitle, body)) !== null && _a !== void 0 ? _a : parseReceptorLaunch(stepTitle, body)) !== null && _b !== void 0 ? _b : parseResourceCheck(body)) !== null && _c !== void 0 ? _c : null);
}
function parsePlainOperational(text) {
    var _a, _b;
    const trimmed = text.trim();
    if (!trimmed)
        return null;
    return (_b = (_a = parseResourceCheck(trimmed)) !== null && _a !== void 0 ? _a : parseJobPending(trimmed)) !== null && _b !== void 0 ? _b : null;
}
function fromStructured(raw, lang = 'en') {
    if (!raw || typeof raw !== 'object')
        return null;
    const o = raw;
    const title = typeof o.title === 'string' ? o.title : '';
    const headline = typeof o.headline === 'string' ? o.headline : '';
    if (!title && !headline)
        return null;
    const rows = Array.isArray(o.rows)
        ? o.rows
            .filter((r) => !!r && typeof r === 'object')
            .map((r) => { var _a, _b; return ({ label: String((_a = r.label) !== null && _a !== void 0 ? _a : ''), value: String((_b = r.value) !== null && _b !== void 0 ? _b : '—') }); })
            .filter((r) => r.label)
        : [];
    const kind = ['reuse', 'launch', 'check', 'pending', 'error', 'info', 'delete'].includes(o.kind)
        ? o.kind
        : 'info';
    const resource = ['receptor', 'peptide', 'docking', 'binding_site', 'screening', 'generic'].includes(o.resource)
        ? o.resource
        : 'generic';
    const notice = {
        kind,
        resource,
        title: title || headline,
        headline: sanitizeHeadline({ kind, resource, title, headline: headline || '', status: typeof o.status === 'string' ? o.status : undefined, rows: filterRows(rows) }),
        status: normalizeStatus(typeof o.status === 'string' ? o.status : undefined),
        rows: filterRows(rows),
    };
    // Always normalize the title from resource+kind so LangGraph generic titles are overridden.
    notice.title = resourceActionLabel(notice, lang);
    return notice;
}
// ── Public API ────────────────────────────────────────────────────────────────
/** Prefer LangGraph `chat_notices[]`; fallback heuristics on ai_response until agent is updated. */
export function resolveChatNotices(data) {
    var _a, _b, _c, _d;
    const lang = data.ui_lang === 'fr' ? 'fr' : 'en';
    const structured = data.chat_notices;
    if (Array.isArray(structured) && structured.length) {
        return structured.map((item) => fromStructured(item, lang)).filter((n) => n != null);
    }
    const notices = [];
    const text = ((_b = (_a = data.ai_response) !== null && _a !== void 0 ? _a : data.message) !== null && _b !== void 0 ? _b : '').trim();
    const snapErr = (_c = data.state_snapshot) === null || _c === void 0 ? void 0 : _c.error_message;
    const err = (_d = data.error) !== null && _d !== void 0 ? _d : (typeof snapErr === 'string' ? snapErr : '');
    if (err) {
        notices.push({
            kind: 'error', resource: 'generic',
            title: 'INFO',
            headline: lang === 'fr' ? "La requête n'a pas abouti" : 'The request did not succeed',
            status: 'failed',
            rows: [{ label: lang === 'fr' ? 'Détail' : 'Detail', value: err }],
        });
    }
    const stepNotice = text ? parseStepBlock(text) : null;
    if (stepNotice) {
        notices.push(stepNotice);
        return notices;
    }
    const plain = text ? parsePlainOperational(text) : null;
    if (plain)
        notices.push(plain);
    return notices;
}
// Short redundant phrases to suppress when a notice card already shows the info.
const SUPPRESS_PHRASES_RE = /résultat\s+(du\s+)?check|résultat\s+du\s+contrôle|contrôle\s+ci-dessus|ci-dessus|voir\s+(la\s+)?carte|voir\s+ci-dessus/i;
/** When notices carry the operational content, avoid duplicating long Step N block in a bubble. */
export function shouldSuppressOperationalBubble(data, notices) {
    var _a;
    if (!notices.length)
        return false;
    const structured = data.chat_notices;
    if (Array.isArray(structured) && structured.length)
        return true;
    const text = ((_a = data.ai_response) !== null && _a !== void 0 ? _a : '').trim();
    if (STEP_BLOCK_RE.test(text))
        return true;
    if (parsePlainOperational(text))
        return true;
    if (SUPPRESS_PHRASES_RE.test(text))
        return true;
    if (data.error)
        return true;
    return false;
}
export function appendChatNotice(notice) {
    var _a;
    const chatHistory = document.getElementById('chat-history');
    if (!chatHistory)
        return;
    const normalized = Object.assign(Object.assign({}, notice), { headline: sanitizeHeadline(notice) });
    const colorClass = noticeColorClass(normalized);
    const resourceLabel = normalized.title || resourceActionLabel(normalized);
    const showPill = Boolean((_a = normalized.status) === null || _a === void 0 ? void 0 : _a.trim());
    const pill = showPill
        ? `<span class="chat-notice-status-pill ${statusPillClass(normalized.status)}">${escapeHtml(normalized.status)}</span>`
        : '';
    const headlineHtml = normalized.headline
        ? `<p class="chat-notice-headline">${formatInlineMarkdown(normalized.headline)}</p>`
        : '';
    const progressRow = normalized.rows.find(r => /^progression$/i.test(r.label));
    const pctMatch = progressRow === null || progressRow === void 0 ? void 0 : progressRow.value.match(/(\d+)\s*%/);
    const pct = pctMatch ? Math.max(0, Math.min(100, parseInt(pctMatch[1], 10))) : null;
    const progressBarHtml = pct != null
        ? `<div class="chat-notice-progress" aria-label="Progression ${pct}%">
         <div class="chat-notice-progress-track">
           <div class="chat-notice-progress-fill" style="width:${pct}%"></div>
         </div>
         <span class="chat-notice-progress-label">${pct}%</span>
       </div>`
        : '';
    const rowsHtml = normalized.rows.length
        ? `<div class="chat-notice-body">${normalized.rows.map(r => `<div class="chat-notice-row">
          <span class="chat-notice-row-label">${escapeHtml(r.label)}</span>
          <strong class="chat-notice-row-value">${formatInlineMarkdown(r.value)}</strong>
        </div>`).join('')}</div>`
        : '';
    const isCompactDelete = normalized.kind === 'delete' &&
        !normalized.headline &&
        !showPill;
    const block = document.createElement('div');
    block.className = `chat-resource-notice ${colorClass}${isCompactDelete ? ' chat-resource-notice-compact' : ''}`;
    block.dataset.noticeKind = normalized.kind;
    block.dataset.noticeResource = normalized.resource;
    block.innerHTML = `
    <div class="chat-notice-header">
      <span class="chat-notice-resource-title">${escapeHtml(resourceLabel)}</span>
      ${pill}
    </div>
    ${headlineHtml}
    ${progressBarHtml}
    ${rowsHtml}
  `;
    chatHistory.appendChild(block);
    chatHistory.scrollTop = chatHistory.scrollHeight;
}
export function appendChatNotices(notices) {
    notices.forEach(appendChatNotice);
}
